import {
  useState,
  RefObject,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useRef,
} from "react";

type CallbackEvent =
  | MouseEvent<HTMLButtonElement>
  | KeyboardEvent<HTMLButtonElement>;

interface UseVideoControlsOptions {
  videoRef?: RefObject<HTMLVideoElement>;
  containerRef?: RefObject<HTMLDivElement>;
  onPlay?: () => void;
  onPause?: () => void;
  onMute?: () => void;
  onUnmute?: () => void;
}

const useVideoControls = ({
  videoRef,
  containerRef,
  onPlay,
  onPause,
  onMute,
  onUnmute,
}: UseVideoControlsOptions) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const prevVolumeRef = useRef(1);

  const onPlayPause = async (e: CallbackEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRef?.current;

    if (video) {
      if (video.paused) {
        try {
          await video.play();
          setIsPlaying(true);
        } catch (error) {
          setIsPlaying(false);
        }
      } else {
        video.pause();
        setIsPlaying(false);
        onPause?.();
      }
    } else {
      if (isPlaying) {
        onPause?.();
        setIsPlaying(false);
      } else {
        onPlay?.();
        setIsPlaying(true);
      }
    }
  };

  const onMuteUnmute = (e: CallbackEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef?.current;
    if (video) {
      const currentlyMuted = video.muted || video.volume === 0;
      const newMuted = !currentlyMuted;

      if (newMuted) {
        if (video.volume > 0) prevVolumeRef.current = video.volume;
        video.muted = true;
        video.volume = 0;
        setIsMuted(true);
        onMute?.();
      } else {
        video.muted = false;
        if (video.volume === 0) {
          video.volume = prevVolumeRef.current || 1;
        }
        setIsMuted(false);
        onUnmute?.();
      }
    } else {
      if (isMuted) {
        onUnmute?.();
        setIsMuted(false);
      } else {
        onMute?.();
        setIsMuted(true);
      }
    }
  };

  const onFullScreen = (e: CallbackEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef?.current;
    const isElementFullScreen = document?.fullscreenElement;

    if (container && typeof document !== "undefined") {
      if (isElementFullScreen) {
        document.exitFullscreen();
        setIsFullScreen(false);
      } else {
        container.requestFullscreen();
        setIsFullScreen(true);
      }
    }
  };

  useEffect(() => {
    const video = videoRef?.current;

    if (video) {
      const computeMuted = (v: HTMLVideoElement) => v.muted || v.volume === 0;

      setIsPlaying(!video.paused);
      setIsMuted(computeMuted(video));

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleLoadedData = () => {
        setIsPlaying(!video.paused);
        setIsMuted(computeMuted(video));
      };
      const handleCanPlay = () => {
        setIsPlaying(!video.paused);
        setIsMuted(computeMuted(video));
      };
      const handleVolumeChange = () => setIsMuted(computeMuted(video));

      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("volumechange", handleVolumeChange);

      const timeoutId = setTimeout(() => {
        if (video) {
          setIsPlaying(!video.paused);
          setIsMuted(computeMuted(video));
        }
      }, 150);

      return () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("volumechange", handleVolumeChange);
        clearTimeout(timeoutId);
      };
    }
  }, [videoRef?.current]);

  return {
    isPlaying,
    isMuted,
    isFullScreen,
    onPlayPause,
    onMuteUnmute,
    onFullScreen,
  };
};

export default useVideoControls;
